/**
 * Form definition schema.
 *
 * A definition is TEMPLATE + per-customer OVERRIDES, resolved once at send time
 * and frozen. See docs/form-definition.md — the freezing is what lets us
 * reconstruct what a customer was actually shown when they confirmed something.
 */

export type SectionKey = 'authorisation' | 'general' | 'agreements' | 'it';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'date'
  | 'number'
  | 'choice'
  /** Multi-select. Value is a string[] of option values. */
  | 'multichoice'
  | 'address'
  | 'abn'
  | 'grid'
  /**
   * A single statement the customer agrees to, as a checkbox. Value is `true`
   * or absent — never a string.
   *
   * Deliberately NOT used for system access itself: see
   * docs/microsoft-integration.md. A tick can authorise us to ask for access;
   * it can never be the access, and it must never be worded as though it is.
   */
  | 'consent'
  /** Read-only outbound link. Records nothing. Used for GDAP. */
  | 'link'
  /** Outbound link plus fields recording it was done elsewhere. Used for MCA. */
  | 'externalAction';

export interface ChoiceOption {
  value: string;
  label: string;
}

export interface GridColumn {
  key: string;
  label: string;
  type: Extract<FieldType, 'text' | 'number' | 'date' | 'choice'>;
  options?: ChoiceOption[];
  width?: number;
}

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
  /** choice and multichoice */
  options?: ChoiceOption[];
  /** grid only */
  columns?: GridColumn[];
  /** link and externalAction only */
  url?: string;
  linkLabel?: string;
  /**
   * `externalAction` only: the fields that record the action was completed
   * somewhere else. The field's value is an object keyed by these fields' keys,
   * or absent while the action has not been done.
   *
   * There is deliberately no "I have done this" tickbox. The record IS the
   * evidence — a tick with no signer details tells us nothing we can hand to
   * our indirect provider, and a self-asserted "done" is exactly the consent
   * checkbox that docs/microsoft-integration.md rejects.
   */
  actionFields?: FieldDefinition[];
  /**
   * `date` only: reject dates after today.
   *
   * Declarative rather than a check in code so it travels inside the frozen
   * definition, like requireExactlyOne. Used for the MCA acceptance date, which
   * Partner Center will not accept in the future.
   */
  notFuture?: boolean;
  /**
   * Every field can be marked "I don't know". Required in the IT section, where
   * partial knowledge is normal and must never block submission.
   */
  allowUnknown?: boolean;
}

export interface GroupDefinition {
  key: string;
  title: string;
  description?: string;
  fields: FieldDefinition[];
  /** Repeating group — locations, contacts. Renders as add/remove cards. */
  repeatable?: boolean;
  addLabel?: string;
  /**
   * Singular noun for one entry, used in validation messages. Without it the
   * group title gets lowercased into things like "add at least one your
   * locations entry", which is not a sentence.
   */
  itemLabel?: string;
  /**
   * Cross-row rule for a repeatable group: exactly one row must carry `value`
   * in its `field`.
   *
   * Declarative rather than a hardcoded check, so it travels inside the frozen
   * definition — a submission can always be re-validated against the rules that
   * were actually in force when it was made.
   *
   * Used for the primary contact. One person has to be the one we address, and
   * two people both marked primary is an ambiguity nobody resolves later.
   */
  requireExactlyOne?: {
    field: string;
    value: string;
    /** Shown when the count is wrong. Written for the customer, not the dev. */
    message: string;
  };
}

export interface SectionDefinition {
  key: SectionKey;
  title: string;
  /** Shown at the top of the section. Plain-spoken, sentence case. */
  intro?: string;
  audience: 'business' | 'it';
  /**
   * Who can actually finish this section.
   *
   * `globalAdmin` means the steps inside it happen in Microsoft's admin centre
   * and need someone holding Global Administrator on the customer's tenant.
   * Said up front rather than discovered halfway through: the person filling
   * in the form is usually not that person, and finding out only when a link
   * refuses them wastes their time and stalls the onboarding.
   *
   * Declarative, so it travels inside the frozen definition and a submission
   * can be re-read knowing what the customer was told.
   */
  completedBy?: 'anyone' | 'globalAdmin';
  groups: GroupDefinition[];
}

export interface FormTemplate {
  version: string;
  sections: SectionDefinition[];
}

/** Per-customer changes, held on the onboarding record until send. */
export interface FormOverrides {
  excludeSections?: SectionKey[];
  /** Dotted `sectionKey.groupKey` */
  excludeGroups?: string[];
  customFields?: Array<{
    sectionKey: SectionKey;
    groupKey: string;
    field: FieldDefinition;
  }>;
  /** Dotted `sectionKey.groupKey.fieldKey` → value */
  prefill?: Record<string, unknown>;
}

/**
 * Template + overrides, computed once at send time. Never changes afterwards —
 * it is stamped on the onboarding and on every submission.
 */
export interface ResolvedDefinition {
  templateVersion: string;
  resolvedAt: string;
  sections: SectionDefinition[];
  prefill: Record<string, unknown>;
}

/** What a customer submits for one section. */
export interface SubmissionPayload {
  sectionKey: SectionKey;
  /** Dotted `groupKey.fieldKey` → value. Repeatable groups hold arrays. */
  values: Record<string, unknown>;
  submittedByName: string;
  submittedByEmail: string;
}

export const UNKNOWN = '__unknown__' as const;

/** Marker written when a customer explicitly says they do not know. */
export interface UnknownValue {
  readonly __unknown: true;
}

export function isUnknown(value: unknown): value is UnknownValue {
  return typeof value === 'object' && value !== null && '__unknown' in value;
}

export function unknownValue(): UnknownValue {
  return { __unknown: true };
}
